import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  buildOfflineEpubBlob,
  fetchOfflineReadyBookMetadata
} from "../utils/appendedChapterEpub";
import {
  cacheOfflineBook,
  isOfflineBookAvailable,
  removeOfflineBook
} from "../utils/offlineLibrary";
import AddToListsModal from "./AddToListsModal";
import "./RecentsRow.css";

let _recentsCache = null;

function RecentsRow() {
  const [recents, setRecents] = useState(() => _recentsCache || []);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [offlineActionId, setOfflineActionId] = useState(null);
  const [listModalBook, setListModalBook] = useState(null);
  const navigate = useNavigate();
  const currentUserId = JSON.parse(localStorage.getItem("user") || "{}").id || 0;
  const rememberToken = localStorage.getItem("rememberToken") || "";

  const fetchRecents = () => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const uid = JSON.parse(localStorage.getItem("user") || "{}").id || 0;
    fetch(`${apiUrl}/get-recents.php?uid=${uid}`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        if (data.success) {
          _recentsCache = Array.isArray(data.recents) ? data.recents : [];
          setRecents(_recentsCache);
        }
      })
      .catch(console.error);
  };

  useEffect(() => {
    fetchRecents();
    // Refresh recents when user changes
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const currentUserId = user.id;

    const handleStorageChange = () => {
      const newUser = JSON.parse(localStorage.getItem("user") || "{}");
      if (newUser.id !== currentUserId) {
        fetchRecents();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") fetchRecents();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("reader-closed", fetchRecents);
    window.addEventListener("reader-progress-saved", fetchRecents);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("reader-closed", fetchRecents);
      window.removeEventListener("reader-progress-saved", fetchRecents);
    };
  }, []);

  const handleMenuToggle = (e, epubId) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === epubId ? null : epubId);
  };

  const handleRemove = (epubId) => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const form = new FormData();
    form.append("epub_id", epubId);
    form.append("uid", currentUserId);
    fetch(`${apiUrl}/remove-recent.php`, {
      method: "POST",
      credentials: "include",
      headers: rememberToken ? { "X-Remember-Token": rememberToken } : undefined,
      body: form
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.success) {
          setRecents((prev) => {
            const next = prev.filter((r) => r.id !== epubId);
            _recentsCache = next;
            return next;
          });
          if (isOfflineBookAvailable(currentUserId, epubId)) {
            removeOfflineBook(currentUserId, epubId).catch(console.error);
          }
          setOpenMenuId(null);
        }
      })
      .catch(console.error);
  };
  const handleOfflineToggle = async (book) => {
    if (!book || !currentUserId) return;

    setOfflineActionId(book.id);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (isOfflineBookAvailable(currentUserId, book.id)) {
        await removeOfflineBook(currentUserId, book.id);
      } else {
        const offlineMetadata = await fetchOfflineReadyBookMetadata({
          apiUrl,
          epubId: book.id,
          userId: currentUserId,
          rememberToken,
          fallback: book
        });
        const blob = await buildOfflineEpubBlob({
          apiUrl,
          epubId: book.id,
          rememberToken
        });
        await cacheOfflineBook({
          userId: currentUserId,
          epubId: book.id,
          metadata: offlineMetadata || book,
          blob
        });
      }
      setOpenMenuId(null);
    } catch (error) {
      console.error("Failed to update offline book:", error);
    } finally {
      setOfflineActionId(null);
    }
  };
  const formatProgressLabel = (progress) => {
    const numeric = Number(progress || 0);
    if (numeric <= 0) return "1%";
    return `${Math.round(numeric)}%`;
  };
  const getProgressWidth = (progress) => {
    const numeric = Number(progress || 0);
    if (numeric <= 0) return "1%";
    return `${Math.min(100, numeric)}%`;
  };

  if (recents.length === 0) return null;

  const selectedBook = recents.find((book) => book.id === openMenuId);
  const selectedBookIsOffline = selectedBook
    ? isOfflineBookAvailable(currentUserId, selectedBook.id)
    : false;

  return (
    <div className="recents-section mt-4">
      {listModalBook && (
        <AddToListsModal
          book={listModalBook}
          onClose={() => setListModalBook(null)}
          onSaved={() => {
            fetchRecents();
          }}
        />
      )}
      <h3 className="recents-heading mb-4" style={{ paddingLeft: "20px" }}>
        Your Recents
      </h3>
      <div className="recents-row">
        {recents.map((epub) => (
          <div key={epub.id} className="recent-card mb-3">
            <div
              className="recent-cover d-flex align-items-center justify-content-center"
              onClick={() => navigate(`/epub/${epub.id}/read`)}
            >
              <span className="recent-cover-letter">
                {epub.title?.charAt(0).toUpperCase()}
              </span>
            </div>

            <div className="recent-meta">
              <span className="recent-percent">
                {formatProgressLabel(epub.progress_percent)}
              </span>
              <div className="recent-menu">
                <span
                  className="material-symbols-rounded recent-menu-btn"
                  onClick={(e) => handleMenuToggle(e, epub.id)}
                >
                  more_horiz
                </span>
              </div>
            </div>

            <div className="recent-progress-track mt-2 mb-3">
              <div
                className="recent-progress-fill"
                style={{ width: getProgressWidth(epub.progress_percent) }}
              />
            </div>

            <h5
              className="recent-title"
              onClick={() => navigate(`/epub/${epub.id}/read`)}
            >
              {epub.title}
            </h5>
            <p className="recent-author">{epub.author}</p>
          </div>
        ))}
      </div>

      {openMenuId && (
        <div
          className="recent-menu-overlay"
          onClick={() => setOpenMenuId(null)}
        >
          <div
            className="recent-menu-sheet"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="recent-menu-item"
              onClick={() => {
                if (!selectedBook) return;
                setListModalBook(selectedBook);
                setOpenMenuId(null);
              }}
            >
              Add to Lists
            </button>
            <button
              className="recent-menu-item"
              onClick={() => {
                navigate(`/epub/${openMenuId}`);
                setOpenMenuId(null);
              }}
            >
              View Details
            </button>
            <button
              className="recent-menu-item"
              onClick={() => handleOfflineToggle(selectedBook)}
              disabled={!selectedBook || offlineActionId === selectedBook.id}
            >
              {offlineActionId === selectedBook?.id
                ? "Working..."
                : selectedBookIsOffline
                  ? "Remove Offline"
                  : "Make Offline"}
            </button>
            <button
              className="recent-menu-item recent-menu-item--danger"
              onClick={() => handleRemove(openMenuId)}
            >
              Delete From Library
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default RecentsRow;
